<template>
  <div class="pago-resultado">
    <div class="card-resultado">
      <template v-if="estado === 'cargando'">
        <div class="spinner-border text-accent mb-3" role="status"></div>
        <p>Verificando el pago...</p>
      </template>

      <template v-else-if="estado === 'ok'">
        <div class="icono-ok">✓</div>
        <h2>{{ esTrial ? '¡Periodo de prueba activado!' : '¡Pago completado!' }}</h2>
        <p v-if="enlace">
          Tu acceso está listo. Usa el enlace para unirte al canal:
        </p>
        <p v-else>
          Tu acceso está siendo procesado. Recibirás un email de confirmación en breve.
        </p>
        <a v-if="enlace" :href="enlace" target="_blank" rel="noopener" class="btn-volver mb-2">
          Acceder al canal →
        </a>
        <a href="/" class="btn-volver btn-secondary">Ir al inicio</a>
      </template>

      <template v-else-if="estado === 'procesando'">
        <div class="icono-info">⏳</div>
        <h2>Pago en proceso</h2>
        <p>Tu pago está siendo confirmado. Recibirás un email cuando esté listo.</p>
        <a href="/" class="btn-volver">Ir al inicio</a>
      </template>

      <template v-else>
        <div class="icono-error">✗</div>
        <h2>Error en el pago</h2>
        <p>{{ errorMsg || 'No se pudo confirmar el pago. Por favor inténtalo de nuevo.' }}</p>
        <a href="/" class="btn-volver">Ir al inicio</a>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "PagoResultado",
  data() {
    return {
      estado: "cargando",
      errorMsg: "",
      enlace: null,
      importe: 0,
      moneda: "EUR",
      esTrial: false,
    };
  },
  async mounted() {
    const params = new URLSearchParams(window.location.search);
    const paymentIntentId           = params.get("payment_intent");
    const paymentIntentClientSecret = params.get("payment_intent_client_secret");
    const setupIntentId             = params.get("setup_intent");
    const setupIntentClientSecret   = params.get("setup_intent_client_secret");
    const redirectStatus            = params.get("redirect_status");
    const referencia                = params.get("ref");

    if (!paymentIntentClientSecret && !setupIntentClientSecret) {
      this.estado   = "error";
      this.errorMsg = "No se recibió información del pago.";
      return;
    }

    if (redirectStatus === "processing") {
      this.estado = "procesando";
      return;
    }

    if (redirectStatus !== "succeeded") {
      this.estado   = "error";
      this.errorMsg = "El pago no se completó correctamente.";
      return;
    }

    const apiBase = process.env.VUE_APP_API_BASE_URL;

    // Setup intent (trial) — confirmar trial en el backend
    if (setupIntentClientSecret) {
      this.esTrial = true;
      try {
        const res = await axios.post(`${apiBase}/api/v2/confirmarTrial`, {
          referencia:      referencia || "",
          setup_intent_id: setupIntentId || "",
        });
        if (res.data?.res === "OK") {
          this.estado = "ok";
          this.enlace = res.data?.enlace || null;
        } else {
          this.estado   = "error";
          this.errorMsg = res.data?.mensaje || res.data?.message || "Error al confirmar el periodo de prueba.";
        }
      } catch (err) {
        const msg = err?.response?.data?.mensaje || err?.response?.data?.message || "";
        if (msg.toLowerCase().includes("idempotente")) {
          this.estado = "ok";
        } else {
          this.estado   = "error";
          this.errorMsg = msg || "Error al verificar el periodo de prueba con el servidor.";
        }
      }
      return;
    }

    // Payment intent (compra normal / suscripción) — confirmar pago en el backend
    try {
      const res = await axios.post(`${apiBase}/api/v2/confirmarPagado`, {
        clientSecret: paymentIntentClientSecret,
        paymentId:    paymentIntentId || "",
        telefono:     "",
      });

      if (res.data?.res === "OK" || res.data?.res === "NOK" && res.data?.message === "Ya confirmado (idempotente)") {
        this.importe = res.data?.importe || 0;
        this.moneda  = res.data?.moneda  || "EUR";
        this.estado  = "ok";
        this.enlace  = res.data?.enlace || null;
      } else {
        this.estado   = "error";
        this.errorMsg = res.data?.mensaje || res.data?.message || "Error al confirmar el pago.";
      }
    } catch (err) {
      // Si el pago ya estaba confirmado (idempotencia) igual mostramos OK
      const msg = err?.response?.data?.mensaje || err?.response?.data?.message || "";
      if (err?.response?.status === 200 || msg.toLowerCase().includes("idempotente")) {
        this.estado = "ok";
        // No disparar pixel en catch idempotente — el pago ya se procesó anteriormente
      } else {
        this.estado   = "error";
        this.errorMsg = msg || "Error al verificar el pago con el servidor.";
      }
    }
  },
};
</script>

<style scoped>
.pago-resultado {
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: var(--q-bg, #0d1b2a);
}
.card-resultado {
  background: var(--q-card, #1a2535);
  border: 1px solid var(--q-border-md, rgba(255,255,255,.12));
  border-radius: 16px;
  padding: 2.5rem 2rem;
  max-width: 420px;
  width: 100%;
  text-align: center;
}
.icono-ok    { font-size: 3rem; color: var(--q-green, #28a745); margin-bottom: 1rem; }
.icono-info  { font-size: 3rem; margin-bottom: 1rem; }
.icono-error { font-size: 3rem; color: var(--q-red, #ef4444); margin-bottom: 1rem; }
h2 { font-size: 1.4rem; font-weight: 700; margin-bottom: 0.75rem; color: var(--q-text, #e7edf5); }
p  { color: var(--q-muted, rgba(231,237,245,.65)); line-height: 1.5; margin-bottom: 1.5rem; }
.btn-volver {
  display: inline-block;
  padding: 0.6rem 1.5rem;
  border-radius: 10px;
  background: var(--q-accent, #00c896);
  color: #04140b;
  font-weight: 700;
  text-decoration: none;
}
.btn-volver.btn-secondary {
  background: transparent;
  border: 1px solid var(--q-border-md, rgba(255,255,255,.2));
  color: var(--q-muted, rgba(231,237,245,.65));
  margin-top: 0.5rem;
}
.btn-volver:hover { opacity: .85; }
.mb-2 { margin-bottom: 0.75rem; }
</style>
